/**
 * Source Smart Editor Store Module
 *
 * Manages source document state and operations for mapping functionality
 *
 * Provides the following features:
 * - Source document content management
 * - Node selection and highlighting
 * - Suggestion mapping and tracking
 * - Content change reactions
 * - Element activation handling
 * - Node ID management
 *
 * @module SourceSmartEditorStore
 */

import { ISmartEditorStore, SmartEditorStore, SOURCE_HIGHLIGHT_COLOR } from '..';
import { isEmpty, last } from 'lodash';
import { EditorMode, IMappingSessionStore } from '../../../stores';
import { reaction } from 'mobx';
import { sanitizeHtml } from '../../../helpers/documentComparisonHelpers';

/**
 * Source Smart Editor Store Interface
 * Defines contract for source document editor operations
 *
 * @interface ISourceSmartEditorStore
 * @extends {ISmartEditorStore}
 * @property {string[]} sourceNodeMap - List of source node IDs with suggestions
 * @property {Function} dispose - Cleanup method for reactions
 */
export interface ISourceSmartEditorStore extends ISmartEditorStore {
  dispose: () => void;
}

/**
 * Source Smart Editor Store
 * Handles source document state and highlighting
 *
 * @class SourceSmartEditorStore
 * @extends {SmartEditorStore}
 *
 * @example
 * // Basic usage
 * const store = new SourceSmartEditorStore(content, mappingSessionStore);
 *
 * @example
 * // Advanced usage
 * const store = new SourceSmartEditorStore(content, mappingSessionStore);
 * store.handleNodeSelection(nodeId);
 */
export class SourceSmartEditorStore extends SmartEditorStore implements ISourceSmartEditorStore {
  protected disposers: (() => void)[] = [];

  constructor(mappingSessionStore: IMappingSessionStore) {
    super(mappingSessionStore);
    this.setupReactions();
  }

  /**
   * Cleanup store resources
   * Disposes all mobx reactions and cleans up event handlers
   *
   * @method dispose
   * @returns {void}
   * @throws {Error} When disposal fails
   *
   * @example
   * // Cleanup when component unmounts
   * store.dispose();
   */
  dispose() {
    this.disposers.forEach((dispose) => dispose());
  }

  /**
   * Handle mapping data loaded event
   * Highlights relevant suggestions in source document
   *
   * @override
   */
  handleMappingDataLoaded = () => {
    this.highlightSuggestions();
  };

  /**
   * Handle element selection in source editor
   * Processes click events and manages element highlighting
   *
   * @method handleElementSelection
   * @param {Element} element - Clicked DOM element
   * @returns {void}
   * @throws {Error} When node ID is invalid
   *
   * @example
   * store.handleElementSelection(domElement);
   */
  handleElementSelection = (element?: Element | null, isMultiSelect = false) => {
    if (this.mappingSessionStore.editorMode === EditorMode.EDITING || !element) {
      !isMultiSelect && this.deactivateSelection();
      return;
    }

    const targetSelections = this.mappingSessionStore.getActiveNodeIds('target');
    const sourceSelection = this.mappingSessionStore.getActiveNodeIds('source');

    if (isMultiSelect && sourceSelection.length + 1 > targetSelections.length) {
      return;
    }

    const selectedElement = this.resolveTableCell(element);
    const nodeId = selectedElement?.getAttribute('data-nodeid');

    if (!nodeId) {
      return;
    }

    const currentNodeId = this.getActiveElement()?.getAttribute('data-nodeid');
    if (currentNodeId === nodeId) {
      return;
    }

    this.mappingSessionStore.clearTransfer();

    if (!isMultiSelect) {
      this.deactivateSelection();
    }

    // Always set the active node
    this.mappingSessionStore.setActiveNode('source', nodeId, isMultiSelect);

    // Only scroll when it's a user interaction (clicking)
    if (!this.isUserInteraction) {
      this.scrollToElement(selectedElement);
    }

    this.setActiveElement(selectedElement);
  };

  deactivateSelection = (nodeId?: string) => {
    let activeNodes: Element[] = [];

    if (nodeId) {
      const activeNode = this.getElementByNodeId(nodeId);
      activeNode && activeNodes.push(activeNode);
    } else {
      activeNodes = Array.from(
        this.editorDocument?.querySelectorAll('[data-highlight="active"]') ?? [],
      );
    }

    if (isEmpty(activeNodes)) return;

    activeNodes.forEach((activeNode) => {
      const currentNodeId = activeNode.getAttribute('data-nodeid');
      const color = this.sourceNodeMap.includes(currentNodeId || '')
        ? SOURCE_HIGHLIGHT_COLOR
        : undefined;

      this.highlightElementWithChildren(activeNode, color);
      this.editorDocument?.getSelection()?.collapse(activeNode);
    });
  };

  /**
   * Highlight all suggestion elements
   * Applies highlighting to all elements with mapped node IDs
   *
   * @method highlightSuggestions
   * @private
   * @returns {void}
   * @throws {Error} When document is not available
   */
  private highlightSuggestions() {
    const doc = this.editorDocument;
    if (!doc) return;

    const activeSourceId = last(this.mappingSessionStore.activeNodeIds.get('source'));

    this.sourceNodeMap.forEach((nodeId) => {
      const element = doc.querySelector(`[data-nodeid="${nodeId}"]`);
      if (!element) return;

      if (!isEmpty(activeSourceId) && nodeId === activeSourceId) {
        this.setActiveElement(element);
        this.scrollToElement(element);
      } else {
        this.highlightElement(element, SOURCE_HIGHLIGHT_COLOR);
      }
    });
  }

  /**
   * Setup MobX reactions
   * Initializes reactions for node and content changes
   *
   * @method setupReactions
   * @private
   * @returns {void}
   * @throws {Error} When reaction setup fails
   */
  private setupReactions() {
    this.disposers.push(
      reaction(
        () => ({
          nodeId: this.mappingSessionStore.derivedSourceNodeId,
          ready: !!this.editorDocument,
        }),
        ({ ready, nodeId }) => {
          if (!ready) return;
          this.handleActiveNodeChange(nodeId);
        },
      ),
      reaction(
        () => ({
          content: this.mappingSessionStore.activeContent.get('source'),
          ready: !!this.editorDocument,
        }),
        ({ ready, content }) => {
          if (!ready || !content) return;
          this.handleSourceContentChange(content);
        },
      ),
      reaction(() => this.mappingSessionStore.editorMode, this.handleEditorModeChange),
    );
  }

  /**
   * Handle active node changes
   * Updates highlighting when active node changes
   *
   * @method handleActiveNodeChange
   * @private
   * @param {string} [nodeId=''] - New active node ID
   * @returns {void}
   * @throws {Error} When node change handling fails
   */
  private handleActiveNodeChange = (nodeId = '') => {
    const isMultiSelect = (this.mappingSessionStore.activeNodeIds.get('target')?.length ?? 0) > 1;

    if (isEmpty(nodeId)) {
      this.deactivateSelection();
      return;
    }

    const selectedElement = this.getElementByNodeId(nodeId);
    this.handleElementSelection(selectedElement, isMultiSelect);
  };

  /**
   * Handle source content changes
   * Updates document content and reapplies highlighting
   *
   * @method handleSourceContentChange
   * @private
   * @param {string} content - New HTML content
   * @returns {void}
   */
  private handleSourceContentChange = (content: string) => {
    if (!content || !this.editorDocument) return;

    // Update content
    const sanitizedContent = sanitizeHtml(content);
    this.editorDocument.body.innerHTML = sanitizedContent;

    this.handleMappingDataLoaded();
  };

  private handleEditorModeChange = (editorMode: EditorMode) => {
    if (editorMode === EditorMode.EDITING) {
      this.deactivateElements();
    }
  };
}
